import { createServerFn } from "@tanstack/react-start";
import { supabaseAnon } from "@/lib/mcp/supabase";

/**
 * Server-side existence checks so missing stories, topics and authors answer
 * with a real 404 status instead of a soft 404 (a "not found" screen served
 * with HTTP 200, which Google reports as "Soft 404").
 */

const parseSlug = (data: unknown): string => {
  const value = typeof data === "string" ? data : (data as { slug?: unknown })?.slug;
  if (typeof value !== "string" || !value.trim()) throw new Error("slug is required");
  return value.trim().slice(0, 300);
};

export const articleExists = createServerFn({ method: "GET" })
  .inputValidator(parseSlug)
  .handler(async ({ data: slug }) => {
    try {
      const db = supabaseAnon();
      const isUuid = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(slug);
      const { data: bySlug, error } = await db
        .from("articles")
        .select("id")
        .eq(isUuid ? "id" : "slug", slug)
        .limit(1)
        .maybeSingle();
      if (error) return true;
      if (bySlug) return true;

      const { data: legacy } = await db
        .from("article_slug_history")
        .select("article_id")
        .eq("old_slug", slug)
        .limit(1)
        .maybeSingle();
      return Boolean(legacy);
    } catch {
      // Never turn an infrastructure hiccup into a 404.
      return true;
    }
  });

export const topicExists = createServerFn({ method: "GET" })
  .inputValidator(parseSlug)
  .handler(async ({ data: slug }) => {
    try {
      const db = supabaseAnon();
      const { data } = await db
        .from("topic_pages")
        .select("slug")
        .eq("slug", slug)
        .limit(1)
        .maybeSingle();
      return Boolean(data);
    } catch {
      return true;
    }
  });

export const authorExists = createServerFn({ method: "GET" })
  .inputValidator(parseSlug)
  .handler(async ({ data: slug }) => {
    try {
      // The site's own byline is always a valid author page.
      if (slug.toLowerCase() === "byteline") return true;

      const db = supabaseAnon();
      const { data: author } = await db
        .from("authors")
        .select("slug")
        .eq("slug", slug)
        .limit(1)
        .maybeSingle();
      if (author) return true;

      // Authors can also come from article bylines, whose slug is derived from
      // the stored name. Match loosely on the same words.
      const pattern = `%${slug.replace(/-/g, "%")}%`;
      const { data: byline } = await db
        .from("articles")
        .select("id")
        .or(`author_name.ilike.${pattern},source_name.ilike.${pattern}`)
        .limit(1)
        .maybeSingle();
      return Boolean(byline);
    } catch {
      return true;
    }
  });
